import { Link } from "react-router-dom";
import { useEffect } from "react";
import PageHeader from "@/components/marketing/PageHeader";
import Reveal from "@/components/Reveal";
import { IMG, SAFETY_IRR } from "@/data/content";
import { trackEvent } from "@/lib/tracking";
import { ShieldCheck, Search, AlertTriangle, Award, Check } from "lucide-react";

export default function SafetyPage() {
  useEffect(() => {
    trackEvent("page_view", { label: "safety" });
  }, []);

  return (
    <div data-testid="safety-page">
      <PageHeader
        overline="Safety & Quality"
        crumb="Safety"
        title="Safety First: Zero Incidents, Every Project, Every Day."
        subtitle="A values-driven safety culture protecting our craft workforce, jobsite partners, and the traveling public."
        image={IMG.safetyBriefing}
        imagePosition="object-[center_65%]"
      />

      <section className="bg-white border-b border-black/10 py-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-8">
          <h2 className="font-heading font-black text-rc-blue text-3xl sm:text-4xl leading-tight">Do the Right Thing</h2>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-5">
          {["Industry-leading EMR performance", "OSHA training and daily job hazard analyses", "Advanced Maintenance of Traffic standards"].map((pillar) => (
            <div key={pillar} className="border-l-4 border-rc-red pl-5 font-heading font-bold text-rc-blue">{pillar}</div>
          ))}
        </div>
      </section>

      <nav aria-label="Safety resources" className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 flex flex-wrap gap-3"><Link to="/safety/certifications" className="rounded-full bg-rc-red text-white px-6 py-3 font-bold">Safety Certifications</Link><Link to="/safety/awards" className="rounded-full border border-rc-red text-rc-red px-6 py-3 font-bold">Safety Awards</Link></nav>

      {/* Responsibility */}
      <section className="bg-rc-bg py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="flex items-center gap-3 text-rc-red mb-5">
              <ShieldCheck size={26} />
              <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight">
                Safety Is Everyone's Responsibility
              </h2>
            </div>
            <p className="text-rc-brown text-lg leading-relaxed">
              Every employee is expected to work safely and take action when unsafe conditions, behaviors, or practices
              are identified. Reporting or correcting potential hazards protects not just the individual, but the entire
              team.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex items-center gap-3 text-rc-red mb-5">
              <Search size={26} />
              <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight">
                Thorough Investigation
              </h2>
            </div>
            <p className="text-rc-brown text-lg leading-relaxed">
              All incidents, accidents and injuries are fully investigated to uncover root causes and help prevent future
              occurrences. Our Incident Review Records (IRRs) are completed collaboratively by the Safety Director and the
              employee's supervisor. Each report includes:
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {SAFETY_IRR.map((i) => (
                <div key={i} className="flex items-center gap-3 bg-white border border-black/10 rounded-xl px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-rc-red/10 text-rc-red flex items-center justify-center shrink-0">
                    <Check size={14} />
                  </span>
                  <span className="text-rc-charcoal text-sm font-medium">{i}</span>
                </div>
              ))}
            </div>
            <p className="text-rc-brown/80 mt-5 text-sm leading-relaxed">
              These reports are reviewed by top management to ensure accountability and continuous safety improvements
              across the organization.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Prevention and leadership */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight mb-5">Stop Work Authority</h2>
            <p className="text-rc-brown text-lg leading-relaxed">Every employee has the authority and responsibility to stop work when an unsafe condition or unsafe act is identified. Work will not resume until the hazard has been evaluated and appropriate controls have been implemented.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight mb-5">Proactive Safety Management</h2>
            <p className="text-rc-brown text-lg leading-relaxed">Ranger's safety program focuses on preventing incidents before they occur through daily Job Hazard Analyses (JHAs), toolbox talks, jobsite inspections, equipment inspections, safety observations, and employee hazard reporting.</p>
          </Reveal>
          <Reveal className="lg:col-span-2">
            <h2 className="font-heading font-black text-rc-blue text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight mb-5">Leadership Commitment</h2>
            <p className="text-rc-brown text-lg leading-relaxed">Safety is a core value at Ranger Construction. Company leadership actively supports safety through field engagement, incident reviews, employee development, and continuous improvement initiatives designed to protect our employees, business partners, and the traveling public.</p>
          </Reveal>
        </div>
      </section>

      {/* Accountability */}
      <section className="bg-rc-charcoal py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 text-rc-red mb-5">
              <AlertTriangle size={26} className="shrink-0" />
              <h2 className="font-heading font-black text-white text-[clamp(1.6rem,3.5vw,2.5rem)] leading-tight">Clear Expectations & Accountability</h2>
            </div>
            <p className="text-white/70 text-lg leading-relaxed">Ranger promotes a culture of accountability through training, coaching, mentoring, and employee engagement. When necessary, violations of established safety requirements may result in disciplinary action.</p>
          </Reveal>
        </div>
      </section>

      {/* Recognition */}
      <section className="bg-rc-charcoal py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-10 flex items-center gap-3">
            <Award className="text-rc-red shrink-0" size={32} />
            <h2 className="font-heading font-black text-white text-[clamp(1.8rem,4vw,3rem)] leading-tight">
              Recognizing Excellence in Safety
            </h2>
          </Reveal>

          <Reveal delay={0.05} className="overflow-hidden rounded-2xl mb-10">
            <img
              src={IMG.safetyTeam}
              alt="Ranger Construction Safety Recognition award winners"
              className="w-full h-auto object-cover"
            />
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-10">
            <Reveal>
              <p className="text-white/75 text-lg leading-relaxed">
                At Ranger Construction, we believe that safety should be celebrated. That's why we created the{" "}
                <span className="text-rc-sand font-semibold">Safety Recognition Program</span> — a company-wide
                initiative that rewards outstanding safety performance across all job sites. Throughout the year, our
                Safety Directors closely monitor every project, tracking progress and identifying teams that consistently
                demonstrate excellence in safety management.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="font-heading font-bold text-white text-xl mb-3">Annual Recognition Event</h3>
              <p className="text-white/75 leading-relaxed">
                Each year, Ranger honors its top-performing supervisors at a dedicated event, where they're recognized
                among their peers for their commitment to safety. This event not only celebrates individual and team
                achievements — it reinforces our safety-first culture and sets the tone for the year ahead. By recognizing
                and rewarding safe practices, we empower our employees to lead by example and keep safety at the forefront
                of everything we do.
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
